import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Paperclip, ShieldCheck, Trash2, GripVertical, MoreVertical, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ACTION_STATUS, EVIDENCE_LABEL, statusClass, statusLabel } from "@/lib/workflow";
import { ApprovalDialog } from "./ApprovalDialog";

function ActionRow({ project, phase, action, users, onChanged, onAttach, highlighted, onHighlighted, dragHandle, onMoveUp, onMoveDown, isFirst, isLast, reordering, canEdit }) {
  const [naOpen, setNaOpen] = useState(false);
  const [naReason, setNaReason] = useState("");
  const [apprOpen, setApprOpen] = useState(false);
  const rowRef = useRef(null);

  useEffect(() => {
    if (highlighted && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(() => onHighlighted?.(), 4000);
      return () => clearTimeout(t);
    }
  }, [highlighted, onHighlighted]);

  const patch = async (body) => {
    try {
      await api.patch(`/projects/${project.project_id}/actions/${action.action_id}`, body);
      await onChanged();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  };
  const changeStatus = (v) => (v === "not_applicable" ? (setNaReason(""), setNaOpen(true)) : patch({ status: v }));
  const confirmNa = async () => {
    if (!naReason.trim()) return toast.error("A short reason is required");
    if (await patch({ status: "not_applicable", na_reason: naReason.trim() })) setNaOpen(false);
  };
  const remove = async () => {
    await api.delete(`/projects/${project.project_id}/actions/${action.action_id}`);
    await onChanged();
  };
  const done = action.status === "completed" || action.status === "not_applicable";
  const needsApproval = action.evidence_required.some((e) => e === "approval" || e === "any");
  const needsFile = action.evidence_required.includes("file");

  return (
    <div ref={rowRef} className={`rounded-lg border p-3 transition-shadow ${highlighted ? "ring-2 ring-blue-500 border-blue-300 bg-blue-50/40" : done ? "border-slate-100 bg-slate-50/60" : "border-slate-200 bg-white"}`} data-testid={`action-row-${action.action_id}`}>
      <div className="flex items-start gap-2">
        {dragHandle}
        <Checkbox className="mt-1" checked={action.status === "completed"} disabled={action.status === "not_applicable"}
          onCheckedChange={(c) => patch({ status: c ? "completed" : "in_progress" })} data-testid={`action-complete-checkbox-${action.action_id}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-sm font-medium ${done ? "line-through text-slate-500" : "text-slate-900"}`}>{action.title}</span>
            {action.mandatory && <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-slate-300">Mandatory</Badge>}
            {action.custom && <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-dashed">Custom</Badge>}
            {action.evidence_required.map((ev) => (
              <Badge key={ev} variant="outline" className={`text-[10px] ${action.evidence.satisfied ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                Evidence: {EVIDENCE_LABEL[ev]} {action.evidence.satisfied ? "✓" : "missing"}
              </Badge>
            ))}
          </div>
          <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3">
            <span>{action.evidence.files} file(s) · {action.evidence.approvals} approval(s)</span>
            {action.completion_date && <span>{action.status === "not_applicable" ? "N/A" : "Completed"} <span className="font-mono">{action.completion_date}</span> by {action.completed_by_name}</span>}
            {action.status === "not_applicable" && action.na_reason && <span className="italic">Reason: {action.na_reason}</span>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {needsApproval && !done && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setApprOpen(true)} data-testid={`action-record-approval-btn-${action.action_id}`}>
                <ShieldCheck className="h-3.5 w-3.5 mr-1" />Record approval
              </Button>
            )}
            {(needsFile || !needsApproval) && !done && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onAttach(action)} data-testid={`action-attach-btn-${action.action_id}`}>
                <Paperclip className="h-3.5 w-3.5 mr-1" />Attach file
              </Button>
            )}
            <Input className="h-7 text-xs bg-transparent border-dashed flex-1 min-w-[160px]" placeholder="Optional notes…" defaultValue={action.notes || ""}
              onBlur={(e) => e.target.value !== (action.notes || "") && patch({ notes: e.target.value })} data-testid={`action-notes-input-${action.action_id}`} />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-end sm:items-start gap-2 shrink-0">
          <Select value={action.owner_id || ""} onValueChange={(v) => patch({ owner_id: v })}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid={`action-owner-select-${action.action_id}`}><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" className="h-8 w-36 text-xs" value={action.due_date || ""} onChange={(e) => patch({ due_date: e.target.value || null })} data-testid={`action-due-input-${action.action_id}`} />
          <Select value={action.status} onValueChange={changeStatus}>
            <SelectTrigger className={`h-8 w-36 text-xs ${statusClass(action.status)}`} data-testid={`action-status-select-${action.action_id}`}><SelectValue>{statusLabel(action.status)}</SelectValue></SelectTrigger>
            <SelectContent>
              {ACTION_STATUS.filter((s) => s.value !== "not_applicable" || action.allow_na).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-500" data-testid={`action-menu-${action.action_id}`} aria-label="Reorder action"><MoreVertical className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={isFirst || reordering} onClick={onMoveUp} data-testid={`action-move-up-${action.action_id}`}><ArrowUp className="h-4 w-4 mr-2" />Move up</DropdownMenuItem>
                  <DropdownMenuItem disabled={isLast || reordering} onClick={onMoveDown} data-testid={`action-move-down-${action.action_id}`}><ArrowDown className="h-4 w-4 mr-2" />Move down</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {action.custom && <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={remove} data-testid={`action-delete-btn-${action.action_id}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>
      </div>

      <Dialog open={naOpen} onOpenChange={setNaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark "{action.title}" as Not Applicable</DialogTitle></DialogHeader>
          <Label className="text-xs">Short reason (required)</Label>
          <Textarea rows={2} value={naReason} onChange={(e) => setNaReason(e.target.value)} placeholder="e.g. Kick-off handled by email, no meeting needed" data-testid="action-na-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNaOpen(false)}>Cancel</Button>
            <Button onClick={confirmNa} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="action-na-confirm-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ApprovalDialog project={project} phase={phase} action={action} open={apprOpen} onClose={() => setApprOpen(false)} onSaved={onChanged} />
    </div>
  );
}

function SortableActionRow({ action, index, items, onMove, reordering, canEdit, ...rest }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: action.action_id, disabled: !canEdit });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined, opacity: isDragging ? 0.85 : 1 };
  const handle = canEdit ? (
    <button {...attributes} {...listeners}
      className="mt-1 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none" aria-label="Drag to reorder"
      data-testid={`action-drag-handle-${action.action_id}`}><GripVertical className="h-4 w-4" /></button>
  ) : null;
  return (
    <div ref={setNodeRef} style={style}>
      <ActionRow action={action} dragHandle={handle} reordering={reordering} canEdit={canEdit}
        isFirst={index === 0} isLast={index === items.length - 1}
        onMoveUp={() => onMove(index, -1)} onMoveDown={() => onMove(index, 1)} {...rest} />
    </div>
  );
}

export const PhaseActions = ({ project, phase, users, onChanged, onAttach, highlightId, onHighlighted }) => {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", owner_id: "", due_date: "", mandatory: true, evidence: "none" });
  const [items, setItems] = useState(phase.actions);
  const [reordering, setReordering] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { setItems(phase.actions); }, [phase.actions]);

  const persist = async (ordered) => {
    const prev = items;
    setItems(ordered); // optimistic
    setReordering(true);
    try {
      await api.post(`/projects/${project.project_id}/phases/${phase.phase_id}/actions/reorder`, { action_ids: ordered.map((a) => a.action_id) });
      await onChanged();
      toast.success("Order saved");
    } catch (e) {
      setItems(prev); // revert on failure
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setReordering(false);
    }
  };

  const onMove = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    persist(arrayMove(items, index, j));
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = items.findIndex((a) => a.action_id === active.id);
    const newI = items.findIndex((a) => a.action_id === over.id);
    if (oldI < 0 || newI < 0) return;
    persist(arrayMove(items, oldI, newI));
  };

  const sortByDue = () => {
    const sorted = [...items].sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1; // actions without due date at the bottom
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date); // ISO dates → earliest first
    });
    persist(sorted);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      await api.post(`/projects/${project.project_id}/phases/${phase.phase_id}/actions`, {
        title: form.title.trim(), owner_id: form.owner_id || phase.owner_id || null, due_date: form.due_date || null,
        mandatory: form.mandatory, evidence_required: form.evidence === "none" ? [] : [form.evidence],
      });
      setOpen(false);
      setForm({ title: "", owner_id: "", due_date: "", mandatory: true, evidence: "none" });
      await onChanged();
      toast.success("Action added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const resolved = items.filter((a) => a.status === "completed" || a.status === "not_applicable").length;

  return (
    <section data-testid="phase-actions-section">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="overline">Actions</p>
          <h3 className="text-base font-semibold text-slate-900">{resolved} / {items.length} resolved</h3>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && <Button variant="ghost" size="sm" onClick={sortByDue} disabled={reordering || items.length < 2} data-testid="phase-sort-by-due-btn" title="Reorder earliest due date first (one-off — does not auto-sort later)">
            <ArrowUpDown className="h-4 w-4 mr-1" />Sort by due date
          </Button>}
          {canEdit && <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="phase-add-action-btn"><Plus className="h-4 w-4 mr-1" />Custom action</Button>}
        </div>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((a) => a.action_id)} strategy={verticalListSortingStrategy}>
          <div className="mt-3 space-y-2">
            {items.map((a, i) => (
              <SortableActionRow key={a.action_id} action={a} index={i} items={items} onMove={onMove} reordering={reordering} canEdit={canEdit}
                project={project} phase={phase} users={users} onChanged={onChanged} onAttach={onAttach}
                highlighted={highlightId === a.action_id} onHighlighted={onHighlighted} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add custom action — {phase.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="custom-action-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Owner (default: phase owner)</Label>
                <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                  <SelectTrigger data-testid="custom-action-owner-select"><SelectValue placeholder={phase.owner_name || "Phase owner"} /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Due date (optional)</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="custom-action-due-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Evidence requirement</Label>
                <Select value={form.evidence} onValueChange={(v) => setForm({ ...form, evidence: v })}>
                  <SelectTrigger data-testid="custom-action-evidence-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="file">File upload</SelectItem>
                    <SelectItem value="approval">Recorded approval</SelectItem>
                    <SelectItem value="any">File or approval</SelectItem>
                  </SelectContent>
                </Select></div>
              <label className="flex items-center gap-2 text-sm pt-6"><Switch checked={form.mandatory} onCheckedChange={(c) => setForm({ ...form, mandatory: !!c })} data-testid="custom-action-mandatory-checkbox" />Mandatory</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="custom-action-save-btn">Add action</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
